// src/helpers/auth.js
import { readOrgChartZoom, saveOrgChartZoom } from "../lib/orgChartZoom";

const API =
  process.env.REACT_APP_API_BASE ||
  process.env.REACT_APP_API_URL ||
  "https://api.bossdrop.ai";

async function j(path, opts = {}) {
  const res = await fetch(API + path, {
    method: "GET",
    ...opts,
    headers: {
      "Content-Type": "application/json",
      ...(opts.headers || {}),
    },
    credentials: "include", // <-- this is the key
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error("http_error");
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const Auth = {
  me: () => j("/auth/me"),
  getPendingMfa: () => j("/auth/mfa/pending"),
  login: ({ email, password }) =>
    j("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    }),
  register: ({ email, password, name }) =>
    j("/auth/register", {
      method: "POST",
      body: JSON.stringify({ email, password, name }),
    }),
  logout: async () => {
    try {
      // keepalive lets it finish even if the page starts unloading
      await j("/auth/logout", { method: "POST", keepalive: true });
    } catch (e) {
      console.warn("logout failed (will still clear local state):", e);
    } finally {
      // Everything goes but the chart's zoom, which says nothing about the workspace.
      const orgChartZoom = readOrgChartZoom();
      localStorage.clear();
      saveOrgChartZoom(orgChartZoom);
    }
    return true;
  },
  requestReset: ({ email, origin, returnTo }) =>
    j("/auth/request-reset", {
      method: "POST",
      body: JSON.stringify({ email, origin, returnTo }),
    }),
  reset: ({ token, password }) =>
    j("/auth/reset", {
      method: "POST",
      body: JSON.stringify({ token, password }),
    }),
  verifyMfa: ({ code, method }) =>
    j("/auth/mfa/verify", {
      method: "POST",
      body: JSON.stringify({ code, method }),
    }),
  resendMfaCode: () =>
    j("/auth/mfa/send-code", {
      method: "POST",
      body: JSON.stringify({}),
    }),
  startTotpSetup: () =>
    j("/auth/mfa/setup/totp/start", {
      method: "POST",
      body: JSON.stringify({}),
    }),
  confirmTotpSetup: ({ code }) =>
    j("/auth/mfa/setup/totp/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  startSmsSetup: ({ phone }) =>
    j("/auth/mfa/setup/sms/start", {
      method: "POST",
      body: JSON.stringify({ phone }),
    }),
  confirmSmsSetup: ({ code }) =>
    j("/auth/mfa/setup/sms/confirm", {
      method: "POST",
      body: JSON.stringify({ code }),
    }),
  // --- Account page (src/pages/Account.js) ---
  // Same shape as everything above: cookies included, and a rejection throws
  // an Error whose .data.error is the server's code.
  updateProfile: ({ name }) =>
    j("/auth/profile", {
      method: "PATCH",
      body: JSON.stringify({ name }),
    }),
  changePassword: ({ currentPassword, newPassword }) =>
    j("/auth/change-password", {
      method: "POST",
      body: JSON.stringify({ currentPassword, newPassword }),
    }),
  // Needs the account password AND a current second factor, so turning it off
  // is never something a borrowed session can do on its own.
  disableMfa: ({ password, code }) =>
    j("/auth/mfa/disable", {
      method: "POST",
      body: JSON.stringify({ password, code }),
    }),
  // Bumps the session epoch: every other browser is signed out, this one is
  // handed a fresh cookie and stays signed in.
  revokeOtherSessions: () =>
    j("/auth/sessions/revoke-others", {
      method: "POST",
      body: JSON.stringify({}),
    }),
  // Rewrites the workspace's access map from the Team directory. OWNER ONLY
  // (an admin gets 403 workspace_owner_only). An EMPTY teamMembers array is
  // REFUSED (400 directory_sync_empty_refused): it is what a failed read looks
  // like, and it used to delete every membership row in the workspace. Emptying
  // the directory on purpose is the separate explicit intent below, which
  // nothing automatic may send. `keepalive` lets a mirror that is already on
  // its way finish if the tab is closed while it is in flight.
  syncWorkspaceMemberships: (
    { teamMembers, clearDirectory = false },
    { keepalive = false } = {},
  ) =>
    j("/auth/workspace-memberships/sync", {
      method: "POST",
      keepalive: keepalive === true,
      body: JSON.stringify(
        clearDirectory === true
          ? { teamMembers, clearDirectory: true }
          : { teamMembers },
      ),
    }),
  // Public: { open: boolean }. Closed unless the server says otherwise.
  signupStatus: () => j("/auth/signup-status"),
  // TIER 1, platform admins only (server checks PLATFORM_ADMIN_EMAILS).
  // Creates the OWNER account for a brand new client company: a whole new
  // customer workspace. Nothing to do with the workspace you are looking at.
  inviteClient: ({ email, name }) =>
    j("/auth/admin/invites", {
      method: "POST",
      body: JSON.stringify({ email, name }),
    }),
  // TIER 1 too, read only: every client workspace owner with its invite,
  // setup, connected tools and automation switches. Resolves to
  // { ok, clients, partial, generatedAt } (backend: server/admin/clientAccounts.js).
  listClients: () => j("/auth/admin/clients"),
  // Implementation specialists (backend: server/specialists/specialistProgram.js),
  // also platform admin only. A refusal carries the server's code on .data.error.
  listSpecialists: () => j("/auth/admin/specialists"),
  createSpecialist: (body) =>
    j("/auth/admin/specialists", { method: "POST", body: JSON.stringify(body) }),
  updateSpecialist: (id, body) =>
    j(`/auth/admin/specialists/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  deleteSpecialist: (id) => j(`/auth/admin/specialists/${encodeURIComponent(id)}`, { method: "DELETE" }),
  sendSpecialistLogin: (id) =>
    j(`/auth/admin/specialists/${encodeURIComponent(id)}/login-link`, { method: "POST", body: "{}" }),
  addSpecialistIntroduction: (id, body) =>
    j(`/auth/admin/specialists/${encodeURIComponent(id)}/introductions`, { method: "POST", body: JSON.stringify(body) }),
  updateSpecialistIntroduction: (id, body) =>
    j(`/auth/admin/specialist-introductions/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  recordSpecialistPayment: (id, body) =>
    j(`/auth/admin/specialist-introductions/${encodeURIComponent(id)}/payments`, { method: "POST", body: JSON.stringify(body) }),
  recordSpecialistPayout: (id, body) =>
    j(`/auth/admin/specialists/${encodeURIComponent(id)}/payouts`, { method: "POST", body: JSON.stringify(body) }),
  // TIER 2, the owner or an admin of a workspace, for THEIR OWN workspace
  // (the server takes the workspace from the session, never from the body).
  // Emails a join link to somebody already listed in that workspace's Team
  // Directory. Resolves to { ok, outcome } where outcome is "invited",
  // "resent" or "already_has_account"; a rejection carries the server's code
  // on .data.error (invalid_email, forbidden_address, not_a_teammate,
  // already_owner, email_failed).
  inviteTeammate: ({ email }) =>
    j("/auth/workspace/invites", {
      method: "POST",
      body: JSON.stringify({ email }),
    }),
  // Who on this workspace's directory has actually signed up:
  // { ok, members: [{ email, hasAccount, hasPassword, invitePending }] }.
  // Same workspace-admin gate, same session-only workspace.
  workspaceJoinStatus: () => j("/auth/workspace/join-status"),
};
